import { Link } from 'react-router';

import { cn } from '@april9au/react-common/utils';

import { SectionHeader } from './section-header';

function isExternalHref(href: string) {
  return /^(mailto:|tel:|https?:)/.test(href);
}

export type ImageCardItem = {
  id: string;
  image: string;
  imageAlt?: string;
  eyebrow?: string;
  /** e.g. "6 min read" — shown next to the eyebrow, not as its own label. */
  readTime?: string;
  title: string;
  description?: string;
  href?: string;
  linkLabel?: string;
};

/**
 * Article/insight card. The first card in a grid renders "featured" — a
 * wider frame with a taller photo — and the rest render "compact"; see
 * `ImageCardGrid` below, which assembles that asymmetric layout.
 */
export function ImageCard({
  item,
  featured = false,
  className,
}: {
  item: ImageCardItem;
  featured?: boolean;
  className?: string;
}) {
  const meta =
    item.eyebrow || item.readTime ? (
      <div className="mb-3.5 flex items-center gap-4">
        {item.eyebrow ? <span className="text-eyebrow">{item.eyebrow}</span> : null}
        {item.readTime ? <span className="text-caption">{item.readTime}</span> : null}
      </div>
    ) : null;

  const content = (
    <>
      <div className={cn('relative w-full overflow-hidden bg-black/5', featured ? 'aspect-[7/3]' : 'aspect-[21/8]')}>
        <img
          src={item.image}
          alt={item.imageAlt ?? item.title}
          className="size-full object-cover transition-transform duration-300 ease-smooth group-hover:scale-105"
        />
      </div>

      <div className={featured ? 'p-9' : 'p-6'}>
        {meta}
        <h3 className={featured ? 'text-h3 mb-3.5' : 'text-navy mb-2.5 text-lg font-normal'}>{item.title}</h3>
        {item.description ? (
          <p className={cn('text-body', featured && 'max-w-[52ch]')}>{item.description}</p>
        ) : null}
        {item.href && featured ? (
          <span className="link-rule link-rule--on-light mt-6">{item.linkLabel ?? 'Read the article'}</span>
        ) : null}
      </div>
    </>
  );

  const card = <div className={cn('card--light group overflow-hidden p-0', className)}>{content}</div>;

  if (!item.href) return card;

  return isExternalHref(item.href) ? (
    <a href={item.href} target="_blank" rel="noreferrer" className="block">
      {card}
    </a>
  ) : (
    <Link to={item.href} className="block">
      {card}
    </Link>
  );
}

/**
 * Grid of `ImageCard`s under a shared section header: the first item runs
 * wide with a large photo, the rest stack alongside it at a smaller scale —
 * this is what keeps a stock-photo article grid from reading as three equal,
 * interchangeable tiles.
 */
export function ImageCardGrid({
  id,
  eyebrow,
  heading,
  description,
  items,
  linkLabel,
  linkHref,
  className,
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  description?: string;
  items: Array<ImageCardItem>;
  /** Optional "All insights"-style link, shown beside the heading. */
  linkLabel?: string;
  linkHref?: string;
  className?: string;
}) {
  const [featuredItem, ...restItems] = items;

  return (
    <section id={id} className={cn('section', className)}>
      <div className="container">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-8">
          <SectionHeader eyebrow={eyebrow} heading={heading} description={description} align="left" className="mb-0" />
          {linkHref ? (
            <Link to={linkHref} className="link-rule link-rule--on-light shrink-0">
              {linkLabel ?? 'All insights'}
            </Link>
          ) : null}
        </div>

        {featuredItem ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
            <ImageCard item={featuredItem} featured />

            {restItems.length ? (
              <div className="flex flex-col gap-6">
                {restItems.map((item) => (
                  <ImageCard key={item.id} item={item} />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
